import { z } from "zod";

import { ClinicCallReason } from "@/packages/shared/src/models/clinic-call-reason";
import { STATE_NAMES } from "@/src/lib/us-states";
import { addressSchema } from "@/src/models/address";
import {
  patientBlockMetadataSchema,
  practitionerBlockMetadataSchema,
  locationBlockMetadataSchema,
  coverageBlockMetadataSchema,
  referralInformationBlockMetadataSchema,
  documentInformationBlockMetadataSchema,
  priorAuthorizationBlockMetadataSchema,
  progressNoteBlockMetadataSchema,
  imagingResultsBlockMetadataSchema,
  labResultsBlockMetadataSchema,
  adminBlockMetadataSchema,
  continuityOfCareBlockMetadataSchema,
  medicalHistoryBlockMetadataSchema,
  medicalRecordRequestBlockMetadataSchema,
  notificationBlockMetadataSchema,
  pharmacyRelatedBlockMetadataSchema,
  emergencySummaryBlockMetadataSchema,
  referralResponseBlockMetadataSchema,
  carePlanBlockMetadataSchema,
  dmeBlockMetadataSchema,
  clearanceRequestBlockMetadataSchema,
  consentBlockMetadataSchema,
  financialBlockMetadataSchema,
  idCardBlockMetadataSchema,
  insuranceBlockMetadataSchema,
  intakeBlockMetadataSchema,
  legalBlockMetadataSchema,
  operationNoteBlockMetadataSchema,
  procedureBlockMetadataSchema,
} from "@/src/models/blocks/block-metadata";
import { BlockKey, BlockType } from "@/src/models/blocks/block-types";
import { nullableSignaturePageNumberSchema, simpleDateSchema } from "@/src/models/common-schema";
import {
  CarePlanFollowUpAttempt,
  DmeOrderType,
  ProcedureCardiacType,
  ProcedureDeviceType,
  ProgressNoteDocumentType,
} from "@/src/models/constants/document-types";
import { FACILITY_TYPE_PROMPT, FacilityType } from "@/src/models/constants/facility-types";
import { ImagingBodyPart, ImagingType } from "@/src/models/constants/imaging-types";
import { LabSpecimenType } from "@/src/models/constants/lab-types";
import {
  ABNORMAL_RESULT_PROMPT,
  BODY_PARTS_PROMPT,
  BODY_PART_LATERALITY_PROMPT,
  IMPRESSION_PROMPT,
  PROGRESS_NOTE_ENCOUNTER_TYPE_PROMPT,
  SCHEDULING_PRIORITY_PROMPT,
} from "@/src/models/constants/prompt";
import { SERVICE_LINE_PROMPT, ServiceLine } from "@/src/models/constants/service-lines";
import { Specialty } from "@/src/models/constants/specialty";
import { coverageSchema } from "@/src/models/coverage";
import { ClaimType, DisabilityDurationType } from "@/src/models/documents/admin/admin";
import { LabResultsValue } from "@/src/models/documents/lab-results/lab-results";
import { medicalHistoryType } from "@/src/models/documents/medical-history/medical-history";
import {
  LegalStatute,
  RequestingLocation,
  SubpoenaPurpose,
} from "@/src/models/documents/medical-records-request/medical-record-request";
import {
  PriorAuthorizationRequestOrResponse,
  PriorAuthorizationStatus,
  PriorAuthorizationType,
  commonProcedureTerminologySchema,
} from "@/src/models/documents/prior-authorization/prior-authorization";
import {
  ProgressNoteFlowSheetType,
  ProgressNoteType,
  SpecialtyFolder,
} from "@/src/models/documents/progress-note/progress-note";
import {
  diagnosisListSchema,
  referralCoreMinimalSchema,
  ReferralResponseType,
  SchedulingPriority,
} from "@/src/models/documents/referral";
import { locationSchema } from "@/src/models/location";
import { resultSchema } from "@/src/models/npi-registry";
import { patientSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

// Extend schemas with id and displayId
export const blockPatientSchema = patientSchema.extend({
  displayId: z.string().nullable(),
  id: z.string().uuid().nullable(),
});
export const blockPractitionerSchema = practitionerSchema.extend({
  displayId: z.string().nullable(),
  id: z.string().uuid().nullable(),
  npiRegistryEntry: resultSchema.nullable(),
  npiNumber: z.string().nullable(),
});
export type BlockPractitioner = z.infer<typeof blockPractitionerSchema>;
export const blockLocationSchema = locationSchema.extend({
  displayId: z.string().nullable(),
  id: z.string().uuid().nullable(),
  placeId: z.string().nullable(),
});
export const blockCoverageSchema = z.intersection(coverageSchema, z.object({ id: z.string().uuid().nullable() }));

// Match result type for entity matching - Represents a potential match with confidence score
export const matchResultSchema = <T extends z.ZodTypeAny>(entitySchema: T) =>
  z.object({
    entity: entitySchema,
    confidence: z.number().min(0).max(1).describe("Matching confidence score from 0-1"),
  });

export type MatchResult<T extends z.ZodTypeAny> = z.infer<ReturnType<typeof matchResultSchema<T>>>;

/**
 * Base block schema with common fields
 */
const baseBlockSchema = z.object({
  // TODO: Make this an object, with field-level extraction confidence scores.
  extractionConfidence: z.record(z.string(), z.number().min(0).max(1)).nullable(),
  // A percentage of blocks are selected for manual review, which will be indicated in the UI.
  // This is determined once, at block-generation time.
  selectedForManualReview: z.boolean().nullable().default(false),
});

// Patient
export const patientBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PATIENT),
  documentExtractedFields: blockPatientSchema,
  initialReviewedFields: blockPatientSchema.nullable(),
  reviewedFields: blockPatientSchema.nullable(),
  matchResults: z.array(matchResultSchema(blockPatientSchema)).default([]),
  metadata: patientBlockMetadataSchema.nullable(),
});
export type PatientBlock = z.infer<typeof patientBlockSchema>;
export type PatientMatchResult = PatientBlock["matchResults"][number];

// Coverage
export const coverageBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.COVERAGE),
  documentExtractedFields: blockCoverageSchema,
  initialReviewedFields: blockCoverageSchema.nullable(),
  reviewedFields: blockCoverageSchema.nullable(),
  matchResults: z.array(matchResultSchema(blockCoverageSchema)).default([]),
  metadata: coverageBlockMetadataSchema.nullable(),
});
export type CoverageBlock = z.infer<typeof coverageBlockSchema>;
export type CoverageMatchResult = CoverageBlock["matchResults"][number];

// Provider
export const providerBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PROVIDER),
  documentExtractedFields: blockPractitionerSchema,
  initialReviewedFields: blockPractitionerSchema.nullable(),
  reviewedFields: blockPractitionerSchema.nullable(),
  matchResults: z.array(matchResultSchema(blockPractitionerSchema)).default([]),
  metadata: practitionerBlockMetadataSchema.nullable(),
});
export type ProviderBlock = z.infer<typeof providerBlockSchema>;
export type ProviderMatchResult = ProviderBlock["matchResults"][number];

// Location
export const locationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.LOCATION),
  isDefaultFallback: z.boolean().optional(),
  documentExtractedFields: blockLocationSchema,
  initialReviewedFields: blockLocationSchema.nullable(),
  reviewedFields: blockLocationSchema.nullable(),
  matchResults: z.array(matchResultSchema(blockLocationSchema)).default([]),
  metadata: locationBlockMetadataSchema.nullable(),
});
export type LocationBlock = z.infer<typeof locationBlockSchema>;
export type LocationMatchResult = LocationBlock["matchResults"][number];

// Referral information
export type ReferralInformationFields = z.infer<typeof referralCoreMinimalSchema>;
export const referralInformationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.REFERRAL_INFORMATION),
  documentExtractedFields: z.lazy(() => referralCoreMinimalSchema),
  initialReviewedFields: z.lazy(() => referralCoreMinimalSchema).nullable(),
  reviewedFields: z.lazy(() => referralCoreMinimalSchema).nullable(),
  selectedForManualReview: z.boolean().nullable().default(false),
  metadata: referralInformationBlockMetadataSchema.nullable(),
});
export type ReferralInformationBlock = z.infer<typeof referralInformationBlockSchema>;

export const appointmentStatusSchema = z
  .string()
  .nullable()
  .describe(
    "Status of an already-scheduled or already-attended appointment referenced in the document — e.g. 'Scheduled', 'Attended', 'Seen'. Informational only; does not drive any routing/authorization logic."
  );

// Document Information is a generic block which encompasses all information that doesn't map to a defined entity
export const documentInformationFieldSchema = z.object({
  ahi: z.string().nullable(),
  appointmentStatus: appointmentStatusSchema,
  authorizationNumber: z.string().nullable(),
  authorizationStatus: z.nativeEnum(PriorAuthorizationStatus).nullable(),
  availableForDownload: z
    .boolean()
    .nullable()
    .describe(
      "Whether the document indicates the requested medical records are available electronically — for example, when an access code, download link, or instructions to retrieve records via a portal are present. False when the document attaches the records directly or otherwise does not require electronic retrieval."
    ),
  bodyPart: z.string().nullable(),
  bodyPartLaterality: z.string().nullable(),
  cannotServicePatient: z.boolean().nullable(),
  cde: z.boolean().nullable(),
  isAddCptRequest: z.boolean().nullable(),
  isDxChangeRequest: z.boolean().nullable(),
  authExpired: z.boolean().nullable(),
  isPpoPlan: z
    .boolean()
    .nullable()
    .describe(
      "Whether the patient's primary insurance coverage named on this prior authorization is a PPO (Preferred Provider Organization) plan, as opposed to an HMO or IPA-managed plan. True when the plan type, payer name, or IPA on the document identifies it as PPO. False when it identifies an HMO/IPA-managed plan. Null if the plan type cannot be determined."
    ),
  isSurgicalAuthorization: z
    .boolean()
    .nullable()
    .describe(
      "Whether this prior authorization is specifically for a surgical procedure/operation, as opposed to an office visit, imaging study, or other non-surgical service. True when the authorized service is a surgery."
    ),
  isEarlySteps: z.boolean().nullable(),
  isInsuranceFollowUp: z.boolean().nullable(),
  isInsuranceAuditRequest: z
    .boolean()
    .nullable()
    .describe(
      "True when the document is a payer/auditor request for chart documentation or records review (e.g. an insurance audit or records request from an insurer), rather than routine insurance correspondence such as a policy update or claim notice."
    ),
  isOtherReferralReason: z.boolean().nullable(),
  recordsRequested: z.boolean().nullable(),
  containsMultiplePatients: z.boolean().nullable(),
  claimType: z.nativeEnum(ClaimType).nullable(),
  clinicName: z.string().nullable(),
  companyName: z.string().nullable(),
  companyNameOther: z
    .string()
    .nullable()
    .describe("Company name written in free text when the company is not one of the companyName options."),
  containsAdmissionLabel: z.boolean().nullable(),
  containsCptCode: z.boolean().nullable(),
  dateOfDocument: simpleDateSchema.nullable(),
  collectionDate: simpleDateSchema.nullable(),
  dateOfEvent: simpleDateSchema.nullable(),
  dateOfProgressNote: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  dateSigned: simpleDateSchema.nullable(),
  diagnosisList: diagnosisListSchema.nullable(),
  diagnosisNameList: z.array(z.string()).nullable(),
  primaryDiagnosis: z
    .string()
    .nullable()
    .describe(
      "The single primary medical reason the patient is being seen — the underlying condition or diagnosis that explains the visit, not a symptom. For example, if diagnosisList captures nausea, headache, and dizziness, primaryDiagnosis should be the condition causing them (e.g., 'Concussion'). Null if the document does not clearly indicate one overriding diagnosis."
    ),
  commonProcedureTerminologyList: z
    .array(commonProcedureTerminologySchema)
    .nullable()
    .describe(
      "CPT/HCPCS procedure or service codes referenced in the document: one entry per code with the code, full text description, and modifier when shown. Use null if no billable procedure codes appear."
    ),
  dischargePlan: z.string().nullable(),
  disabilityDurationType: z.nativeEnum(DisabilityDurationType).nullable(),
  dispositionReason: z.string().nullable(),
  documentType: z.string().nullable(),
  requestOrResponse: z.nativeEnum(PriorAuthorizationRequestOrResponse).nullable(),
  dmeType: z.string().nullable(),
  dmeOrderType: z.nativeEnum(DmeOrderType).nullable(),
  procedureCardiacType: z.nativeEnum(ProcedureCardiacType).nullable(),
  procedureDeviceType: z.nativeEnum(ProcedureDeviceType).nullable(),
  equipmentOrSupply: z.string().nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  flowSheetType: z.nativeEnum(ProgressNoteFlowSheetType).nullable(),
  hospitalAbbrev: z.string().nullable(),
  imagingType: z.string().nullable(),
  impression: z.string().nullable().describe(IMPRESSION_PROMPT),
  insuranceCompanyName: z.string().nullable(),
  intakeNote: z.string().nullable(),
  isDischarge: z.boolean().nullable(),
  isIcd: z.boolean().nullable(),
  isNewborn: z.boolean().nullable(),
  followUpAttempt: z.nativeEnum(CarePlanFollowUpAttempt).nullable(),
  isScored: z.boolean().nullable(),
  isSigned: z.boolean().nullable(),
  isSkilledNursingOrHomeHealth: z.boolean().nullable(),
  isStockings: z.boolean().nullable(),
  isSummaryOfCare: z.boolean().nullable(),
  labTypesAndResults: z
    .array(
      z.object({
        typeOfLab: z.string().nullable(),
        labResult: z.nativeEnum(LabResultsValue).nullable(),
      })
    )
    .nullable(),
  legalStatute: z.nativeEnum(LegalStatute).nullable(),
  listOfNameOfLabs: z.array(z.string()).nullable(),
  locationName: z.string().nullable(),
  medicationName: z.string().nullable(),
  supplyDuration: z.string().nullable(),
  medications: z.array(z.string()).nullable(),
  medicalHistoryType: z.nativeEnum(medicalHistoryType).nullable(),
  nameOfDocument: z.string().nullable(),
  nameOfIllness: z.string().nullable(),
  nameOfLab: z.string().nullable(),
  isOperation: z.boolean().nullable(),
  orderNumber: z.string().nullable(),
  transferReason: z.string().nullable(),
  incompleteRoi: z.string().nullable(),
  lwcImmunization: z.boolean().nullable(),
  patientLogType: z.string().nullable(),
  usStateName: z.nativeEnum(STATE_NAMES).nullable(),
  procedure: z.string().nullable(),
  progressNoteType: z.nativeEnum(ProgressNoteType).nullable(),
  clinicCallReason: z.nativeEnum(ClinicCallReason).nullable(),
  reasonForReferral: z.string().nullable(),
  reasonForVisit: z.string().nullable(),
  recommendation: z.string().nullable(),
  referralResponse: z.nativeEnum(ReferralResponseType).nullable(),
  requestingLocation: z.nativeEnum(RequestingLocation).nullable(),
  result: z.nativeEnum(LabResultsValue).nullable(),
  resultsPresent: z.boolean().nullable(),
  schedulingPriority: z.nativeEnum(SchedulingPriority).nullable().describe(SCHEDULING_PRIORITY_PROMPT),
  senderName: z.string().nullable(),
  faxAddresseeFamilyName: z
    .string()
    .nullable()
    .describe(
      "The family (last) name of the person a fax/document's 'To' line or cover-sheet addressee is directed to, extracted as plain text regardless of whether they are a licensed provider — e.g. a nurse or other staff member with no NPI. Distinct from the receivingProvider block, which only surfaces a name that matches a known practitioner."
    ),
  faxAddresseeGivenName: z
    .string()
    .nullable()
    .describe(
      "The given (first) name of the person a fax/document's 'To' line or cover-sheet addressee is directed to. See faxAddresseeFamilyName."
    ),
  signaturePageNumber: nullableSignaturePageNumberSchema,
  signatureRequired: z.boolean().nullable(),
  significantFindings: z.boolean().nullable(),
  abnormal: z.boolean().nullable().describe(ABNORMAL_RESULT_PROMPT),
  specialty: z.nativeEnum(Specialty).nullable(),
  specialties: z.array(z.nativeEnum(Specialty)).nullable(),
  specialtyFolder: z.nativeEnum(SpecialtyFolder).nullable(),
  specialtyOrFacilityName: z.string().nullable(),
  studyType: z.string().nullable(),
  subpoenaPurpose: z.nativeEnum(SubpoenaPurpose).nullable(),
  summaryStatements: z.string().nullable(),
  surgeryName: z.string().nullable(),
  testNotPerformed: z.boolean().nullable(),
  specimenCollected: z.boolean().nullable(),
  typeOfLab: z.string().nullable(),
  vaccineType: z.string().nullable(),
  hasMultipleDates: z
    .boolean()
    .nullable()
    .describe(
      "Whether the document contains records from multiple different dates or encounters. If the document spans multiple visit dates (e.g., a chart with entries from different days), set to true. If it covers a single encounter or date, set to false."
    ),
});
export type DocumentInformationFields = z.infer<typeof documentInformationFieldSchema>;
export const documentInformationBlockSchema = z.object({
  type: z.literal(BlockType.DOCUMENT_INFORMATION),
  documentExtractedFields: z.lazy(() => documentInformationFieldSchema),
  initialReviewedFields: z.lazy(() => documentInformationFieldSchema).nullable(),
  reviewedFields: z.lazy(() => documentInformationFieldSchema).nullable(),
  selectedForManualReview: z.boolean().nullable().default(false),
  metadata: documentInformationBlockMetadataSchema.nullable(),
});
export type DocumentInformationBlock = z.infer<typeof documentInformationBlockSchema>;

// Prior authorization fields schema
export const priorAuthorizationFieldSchema = z.object({
  requestId: z.string().nullable(),
  authorizationNumber: z
    .string()
    .nullable()
    .describe(
      `The final approval number/certification code assigned to this authorization upon approval.
      Look for labels like "Authorization", "Auth #", "Auth Number", "Pre-Auth", "Prior Auth",
      "Certification Number", "Cert #". Return only the alphanumeric identifier (may include hyphens).
      Do not confuse with Policy ID, Group Number, Member ID, Subscriber ID, or request/tracking IDs.`
    ),
  authorizationType: z.nativeEnum(PriorAuthorizationType).nullable(),
  authorizationStatus: z.nativeEnum(PriorAuthorizationStatus).nullable(),
  authorizationFor: z.string().nullable(),
  commonProcedureTerminologyList: z.array(commonProcedureTerminologySchema).nullable(),
  startDate: simpleDateSchema.nullable(),
  endDate: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  dateOfDocument: simpleDateSchema.nullable(),
  dateOfEvent: simpleDateSchema.nullable(),
  medicationName: z.string().nullable(),
  supplyDuration: z.string().nullable(),
  numberOfServices: z.number().nullable(),
  specialty: z.nativeEnum(Specialty).nullable(),
});

// Prior authorization block schema
export type PriorAuthorizationFields = z.infer<typeof priorAuthorizationFieldSchema>;
export const priorAuthorizationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PRIOR_AUTHORIZATION),
  documentExtractedFields: z.lazy(() => priorAuthorizationFieldSchema),
  initialReviewedFields: z.lazy(() => priorAuthorizationFieldSchema).nullable(),
  reviewedFields: z.lazy(() => priorAuthorizationFieldSchema).nullable(),
  metadata: priorAuthorizationBlockMetadataSchema.nullable(),
});
export type PriorAuthorizationBlock = z.infer<typeof priorAuthorizationBlockSchema>;

// Progress note fields schema
export const progressNoteFieldSchema = z.object({
  dateOfProgressNote: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  type: z.nativeEnum(ProgressNoteDocumentType).nullable(),
  progressNoteType: z.nativeEnum(ProgressNoteType).nullable(),
  encounterType: z.string().nullable().describe(PROGRESS_NOTE_ENCOUNTER_TYPE_PROMPT),
  dateOfEvent: simpleDateSchema.nullable(),
  reasonForVisit: z.string().nullable(),
  reasonForReferral: z.string().nullable(),
  diagnosisList: diagnosisListSchema.nullable(),
  specialty: z.nativeEnum(Specialty).nullable(),
  specialtyFolder: z.nativeEnum(SpecialtyFolder).nullable(),
  orderNumber: z.string().nullable(),
  surgeryName: z.string().nullable(),
  hospitalAbbrev: z.string().nullable(),
  signatureRequired: z.boolean().nullable(),
  flowSheetType: z.nativeEnum(ProgressNoteFlowSheetType).nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  isSummaryOfCare: z.boolean().nullable(),
  serviceLine: z.nativeEnum(ServiceLine).nullable().describe(SERVICE_LINE_PROMPT),
  locationName: z.string().nullable(),
  isOperation: z.boolean().nullable(),
  isConsult: z.boolean().nullable(),
  isTelemedicine: z.boolean().nullable(),
  isDischarge: z.boolean().nullable(),
  isIfsp: z.boolean().nullable(),
  clinicName: z.string().nullable(),
  cde: z.boolean().nullable(),
});

// Progress note block schema
export type ProgressNoteFields = z.infer<typeof progressNoteFieldSchema>;
export const progressNoteBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PROGRESS_NOTE),
  documentExtractedFields: z.lazy(() => progressNoteFieldSchema),
  initialReviewedFields: z.lazy(() => progressNoteFieldSchema).nullable(),
  reviewedFields: z.lazy(() => progressNoteFieldSchema).nullable(),
  metadata: progressNoteBlockMetadataSchema.nullable(),
});
export type ProgressNoteBlock = z.infer<typeof progressNoteBlockSchema>;

// Imaging results fields schema
export const imagingResultsFieldSchema = z.object({
  dateOfImagingResults: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  dateOfEvent: simpleDateSchema.nullable(),
  imagingType: z.nativeEnum(ImagingType).nullable(),
  studyType: z.string().nullable(),
  bodyPart: z.string().nullable(),
  bodyParts: z.array(z.nativeEnum(ImagingBodyPart)).nullable().describe(BODY_PARTS_PROMPT),
  bodyPartLaterality: z.string().nullable().describe(BODY_PART_LATERALITY_PROMPT),
  procedure: z.string().nullable(),
  needsSignature: z.boolean().nullable(),
  needsClarification: z.boolean().nullable(),
  significantFindings: z.boolean().nullable(),
  isPretesting: z.boolean().nullable(),
});

// Imaging results block schema
export type ImagingResultsFields = z.infer<typeof imagingResultsFieldSchema>;
export const imagingResultsBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.IMAGING_RESULTS),
  documentExtractedFields: z.lazy(() => imagingResultsFieldSchema),
  initialReviewedFields: z.lazy(() => imagingResultsFieldSchema).nullable(),
  reviewedFields: z.lazy(() => imagingResultsFieldSchema).nullable(),
  metadata: imagingResultsBlockMetadataSchema.nullable(),
});
export type ImagingResultsBlock = z.infer<typeof imagingResultsBlockSchema>;

// Lab results fields schema
export const labResultsFieldSchema = z.object({
  dateOfLabResults: simpleDateSchema.nullable(),
  dateReceived: simpleDateSchema.nullable(),
  dateOfEvent: simpleDateSchema.nullable(),
  collectionDate: simpleDateSchema.nullable(),
  nameOfLab: z.string().nullable(),
  listOfNameOfLabs: z.array(z.string()).nullable(),
  typeOfLab: z.string().nullable(),
  labResult: z.nativeEnum(LabResultsValue).nullable(),
  labTypesAndResults: z
    .array(
      z.object({
        typeOfLab: z.string().nullable(),
        labResult: z.nativeEnum(LabResultsValue).nullable(),
      })
    )
    .nullable(),
  bodyPart: z.string().nullable(),
  needsSignature: z.boolean().nullable(),
  facilityType: z.nativeEnum(FacilityType).nullable().describe(FACILITY_TYPE_PROMPT),
  testNotPerformed: z.boolean().nullable(),
  specimenCollected: z.boolean().nullable(),
  specimenType: z.nativeEnum(LabSpecimenType).nullable(),
  resultsPresent: z.boolean().nullable(),
  isOrderedBySpecialist: z.boolean().nullable(),
  isNewborn: z.boolean().nullable(),
  isPretesting: z.boolean().nullable(),
  isLabAddOn: z.boolean().nullable(),
});

// Lab results block schema
export type LabResultsFields = z.infer<typeof labResultsFieldSchema>;
export const labResultsBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.LAB_RESULTS),
  documentExtractedFields: z.lazy(() => labResultsFieldSchema),
  initialReviewedFields: z.lazy(() => labResultsFieldSchema).nullable(),
  reviewedFields: z.lazy(() => labResultsFieldSchema).nullable(),
  metadata: labResultsBlockMetadataSchema.nullable(),
});
export type LabResultsBlock = z.infer<typeof labResultsBlockSchema>;

// Admin fields schema
export const adminFieldSchema = documentInformationFieldSchema.pick({
  authorizationStatus: true,
  bodyPart: true,
  claimType: true,
  companyName: true,
  dateOfDocument: true,
  dateOfEvent: true,
  dateReceived: true,
  disabilityDurationType: true,
  documentType: true,
  equipmentOrSupply: true,
  facilityType: true,
  isSigned: true,
  nameOfDocument: true,
  nameOfIllness: true,
  schedulingPriority: true,
  signaturePageNumber: true,
  signatureRequired: true,
  specialtyOrFacilityName: true,
});

export type AdminFields = z.infer<typeof adminFieldSchema>;
export const adminBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.ADMIN),
  documentExtractedFields: z.lazy(() => adminFieldSchema),
  initialReviewedFields: z.lazy(() => adminFieldSchema).nullable(),
  reviewedFields: z.lazy(() => adminFieldSchema).nullable(),
  metadata: adminBlockMetadataSchema.nullable(),
});
export type AdminBlock = z.infer<typeof adminBlockSchema>;

// Continuity of Care fields schema
export const continuityOfCareFieldSchema = documentInformationFieldSchema.pick({
  authorizationStatus: true,
  companyName: true,
  dateOfDocument: true,
  dateOfEvent: true,
  dateReceived: true,
  diagnosisList: true,
  documentType: true,
  facilityType: true,
  hospitalAbbrev: true,
  orderNumber: true,
  schedulingPriority: true,
  signatureRequired: true,
  specialtyFolder: true,
});

export type ContinuityOfCareFields = z.infer<typeof continuityOfCareFieldSchema>;
export const continuityOfCareBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.CONTINUITY_OF_CARE),
  documentExtractedFields: z.lazy(() => continuityOfCareFieldSchema),
  initialReviewedFields: z.lazy(() => continuityOfCareFieldSchema).nullable(),
  reviewedFields: z.lazy(() => continuityOfCareFieldSchema).nullable(),
  metadata: continuityOfCareBlockMetadataSchema.nullable(),
});
export type ContinuityOfCareBlock = z.infer<typeof continuityOfCareBlockSchema>;

// Medical History fields schema
export const medicalHistoryFieldSchema = documentInformationFieldSchema.pick({
  availableForDownload: true,
  dateOfEvent: true,
  dateReceived: true,
  isSigned: true,
});

export type MedicalHistoryFields = z.infer<typeof medicalHistoryFieldSchema>;
export const medicalHistoryBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.MEDICAL_HISTORY),
  documentExtractedFields: z.lazy(() => medicalHistoryFieldSchema),
  initialReviewedFields: z.lazy(() => medicalHistoryFieldSchema).nullable(),
  reviewedFields: z.lazy(() => medicalHistoryFieldSchema).nullable(),
  metadata: medicalHistoryBlockMetadataSchema.nullable(),
});
export type MedicalHistoryBlock = z.infer<typeof medicalHistoryBlockSchema>;

// Medical Record Request fields schema
export const medicalRecordRequestFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  dateReceived: true,
  incompleteRoi: true,
  legalStatute: true,
  lwcImmunization: true,
  requestingLocation: true,
  schedulingPriority: true,
  specialty: true,
  subpoenaPurpose: true,
  transferReason: true,
});

export type MedicalRecordRequestFields = z.infer<typeof medicalRecordRequestFieldSchema>;
export const medicalRecordRequestBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.MEDICAL_RECORD_REQUEST),
  documentExtractedFields: z.lazy(() => medicalRecordRequestFieldSchema),
  initialReviewedFields: z.lazy(() => medicalRecordRequestFieldSchema).nullable(),
  reviewedFields: z.lazy(() => medicalRecordRequestFieldSchema).nullable(),
  metadata: medicalRecordRequestBlockMetadataSchema.nullable(),
});
export type MedicalRecordRequestBlock = z.infer<typeof medicalRecordRequestBlockSchema>;

// Notification fields schema
export const notificationEncounterTypeSchema = z.enum(["Admit", "Discharge"]);
export type NotificationEncounterType = z.infer<typeof notificationEncounterTypeSchema>;
export const notificationFieldSchema = documentInformationFieldSchema
  .pick({
    dateOfDocument: true,
    dateOfEvent: true,
    facilityType: true,
    isNewborn: true,
    nameOfLab: true,
  })
  .extend({
    encounterType: notificationEncounterTypeSchema.nullable(),
  });

export type NotificationFields = z.infer<typeof notificationFieldSchema>;
export const notificationBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.NOTIFICATION),
  documentExtractedFields: z.lazy(() => notificationFieldSchema),
  initialReviewedFields: z.lazy(() => notificationFieldSchema).nullable(),
  reviewedFields: z.lazy(() => notificationFieldSchema).nullable(),
  metadata: notificationBlockMetadataSchema.nullable(),
});
export type NotificationBlock = z.infer<typeof notificationBlockSchema>;

// Pharmacy Related fields schema
export const pharmacyRelatedFieldSchema = documentInformationFieldSchema.pick({
  dateOfDocument: true,
  dateOfEvent: true,
  dateReceived: true,
  medicationName: true,
  supplyDuration: true,
  medications: true,
});

export type PharmacyRelatedFields = z.infer<typeof pharmacyRelatedFieldSchema>;
export const pharmacyRelatedBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PHARMACY_RELATED),
  documentExtractedFields: z.lazy(() => pharmacyRelatedFieldSchema),
  initialReviewedFields: z.lazy(() => pharmacyRelatedFieldSchema).nullable(),
  reviewedFields: z.lazy(() => pharmacyRelatedFieldSchema).nullable(),
  metadata: pharmacyRelatedBlockMetadataSchema.nullable(),
});
export type PharmacyRelatedBlock = z.infer<typeof pharmacyRelatedBlockSchema>;

// Emergency Summary fields schema
export const emergencySummaryFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  facilityType: true,
  containsAdmissionLabel: true,
  dispositionReason: true,
  isDischarge: true,
  isSummaryOfCare: true,
  isNewborn: true,
  specialty: true,
  diagnosisList: true,
  diagnosisNameList: true,
  primaryDiagnosis: true,
  locationName: true,
  dischargePlan: true,
});

export type EmergencySummaryFields = z.infer<typeof emergencySummaryFieldSchema>;
export const emergencySummaryBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.EMERGENCY_SUMMARY),
  documentExtractedFields: z.lazy(() => emergencySummaryFieldSchema),
  initialReviewedFields: z.lazy(() => emergencySummaryFieldSchema).nullable(),
  reviewedFields: z.lazy(() => emergencySummaryFieldSchema).nullable(),
  metadata: emergencySummaryBlockMetadataSchema.nullable(),
});
export type EmergencySummaryBlock = z.infer<typeof emergencySummaryBlockSchema>;

// Referral Response fields schema
export const referralResponseFieldSchema = documentInformationFieldSchema
  .pick({
    dateReceived: true,
    documentType: true,
    referralResponse: true,
  })
  .extend({
    dateOfReferral: simpleDateSchema.nullable(),
    dateOfEvent: simpleDateSchema.nullable(),
    reasonForReferral: z.string().nullable(),
    specialty: z.nativeEnum(Specialty).nullable(),
    clinicName: z.string().nullable(),
  });

export type ReferralResponseFields = z.infer<typeof referralResponseFieldSchema>;
export const referralResponseBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.REFERRAL_RESPONSE),
  documentExtractedFields: z.lazy(() => referralResponseFieldSchema),
  initialReviewedFields: z.lazy(() => referralResponseFieldSchema).nullable(),
  reviewedFields: z.lazy(() => referralResponseFieldSchema).nullable(),
  metadata: referralResponseBlockMetadataSchema.nullable(),
});
export type ReferralResponseBlock = z.infer<typeof referralResponseBlockSchema>;

// Care Plan fields schema
export const carePlanFieldSchema = documentInformationFieldSchema
  .pick({
    clinicName: true,
    dateOfDocument: true,
    dateOfEvent: true,
    dateReceived: true,
    documentType: true,
    followUpAttempt: true,
    signaturePageNumber: true,
  })
  .extend({
    serviceLine: z.nativeEnum(ServiceLine).nullable().describe(SERVICE_LINE_PROMPT),
  });

export type CarePlanFields = z.infer<typeof carePlanFieldSchema>;
export const carePlanBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.CARE_PLAN),
  documentExtractedFields: z.lazy(() => carePlanFieldSchema),
  initialReviewedFields: z.lazy(() => carePlanFieldSchema).nullable(),
  reviewedFields: z.lazy(() => carePlanFieldSchema).nullable(),
  metadata: carePlanBlockMetadataSchema.nullable(),
});
export type CarePlanBlock = z.infer<typeof carePlanBlockSchema>;

// DME fields schema
export const dmeFieldSchema = documentInformationFieldSchema.pick({
  authorizationStatus: true,
  dateOfDocument: true,
  dateOfEvent: true,
  dateReceived: true,
  dmeType: true,
});

export type DmeFields = z.infer<typeof dmeFieldSchema>;
export const dmeBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.DME),
  documentExtractedFields: z.lazy(() => dmeFieldSchema),
  initialReviewedFields: z.lazy(() => dmeFieldSchema).nullable(),
  reviewedFields: z.lazy(() => dmeFieldSchema).nullable(),
  metadata: dmeBlockMetadataSchema.nullable(),
});
export type DmeBlock = z.infer<typeof dmeBlockSchema>;

// Clearance Request fields schema
export const clearanceRequestFieldSchema = documentInformationFieldSchema.pick({
  clinicName: true,
  companyName: true,
  dateOfEvent: true,
  dateReceived: true,
  dateSigned: true,
  faxAddresseeFamilyName: true,
  faxAddresseeGivenName: true,
  schedulingPriority: true,
  signatureRequired: true,
  specialty: true,
  surgeryName: true,
});

export type ClearanceRequestFields = z.infer<typeof clearanceRequestFieldSchema>;
export const clearanceRequestBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.CLEARANCE_REQUEST),
  documentExtractedFields: z.lazy(() => clearanceRequestFieldSchema),
  initialReviewedFields: z.lazy(() => clearanceRequestFieldSchema).nullable(),
  reviewedFields: z.lazy(() => clearanceRequestFieldSchema).nullable(),
  metadata: clearanceRequestBlockMetadataSchema.nullable(),
});
export type ClearanceRequestBlock = z.infer<typeof clearanceRequestBlockSchema>;

// Consent fields schema
export const consentFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  dateReceived: true,
  dateSigned: true,
  nameOfDocument: true,
});

export type ConsentFields = z.infer<typeof consentFieldSchema>;
export const consentBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.CONSENT),
  documentExtractedFields: z.lazy(() => consentFieldSchema),
  initialReviewedFields: z.lazy(() => consentFieldSchema).nullable(),
  reviewedFields: z.lazy(() => consentFieldSchema).nullable(),
  metadata: consentBlockMetadataSchema.nullable(),
});
export type ConsentBlock = z.infer<typeof consentBlockSchema>;

// Financial fields schema
export const financialFieldSchema = documentInformationFieldSchema.pick({
  authorizationStatus: true,
  companyName: true,
  dateReceived: true,
  documentType: true,
  signatureRequired: true,
});

export type FinancialFields = z.infer<typeof financialFieldSchema>;
export const financialBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.FINANCIAL),
  documentExtractedFields: z.lazy(() => financialFieldSchema),
  initialReviewedFields: z.lazy(() => financialFieldSchema).nullable(),
  reviewedFields: z.lazy(() => financialFieldSchema).nullable(),
  metadata: financialBlockMetadataSchema.nullable(),
});
export type FinancialBlock = z.infer<typeof financialBlockSchema>;

// ID Card fields schema
export const idCardFieldSchema = documentInformationFieldSchema.pick({
  dateReceived: true,
});

export type IdCardFields = z.infer<typeof idCardFieldSchema>;
export const idCardBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.ID_CARD),
  documentExtractedFields: z.lazy(() => idCardFieldSchema),
  initialReviewedFields: z.lazy(() => idCardFieldSchema).nullable(),
  reviewedFields: z.lazy(() => idCardFieldSchema).nullable(),
  metadata: idCardBlockMetadataSchema.nullable(),
});
export type IdCardBlock = z.infer<typeof idCardBlockSchema>;

// Insurance fields schema
export const insuranceFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  dateReceived: true,
  insuranceCompanyName: true,
  signatureRequired: true,
  isInsuranceAuditRequest: true,
  containsMultiplePatients: true,
});

export type InsuranceFields = z.infer<typeof insuranceFieldSchema>;
export const insuranceBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.INSURANCE),
  documentExtractedFields: z.lazy(() => insuranceFieldSchema),
  initialReviewedFields: z.lazy(() => insuranceFieldSchema).nullable(),
  reviewedFields: z.lazy(() => insuranceFieldSchema).nullable(),
  metadata: insuranceBlockMetadataSchema.nullable(),
});
export type InsuranceBlock = z.infer<typeof insuranceBlockSchema>;

// Intake fields schema
export const intakeFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  dateReceived: true,
  intakeNote: true,
});

export type IntakeFields = z.infer<typeof intakeFieldSchema>;
export const intakeBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.INTAKE),
  documentExtractedFields: z.lazy(() => intakeFieldSchema),
  initialReviewedFields: z.lazy(() => intakeFieldSchema).nullable(),
  reviewedFields: z.lazy(() => intakeFieldSchema).nullable(),
  metadata: intakeBlockMetadataSchema.nullable(),
});
export type IntakeBlock = z.infer<typeof intakeBlockSchema>;

// Legal fields schema
export const legalFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  dateReceived: true,
  dateSigned: true,
  documentType: true,
  signatureRequired: true,
});

export type LegalFields = z.infer<typeof legalFieldSchema>;
export const legalBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.LEGAL),
  documentExtractedFields: z.lazy(() => legalFieldSchema),
  initialReviewedFields: z.lazy(() => legalFieldSchema).nullable(),
  reviewedFields: z.lazy(() => legalFieldSchema).nullable(),
  metadata: legalBlockMetadataSchema.nullable(),
});
export type LegalBlock = z.infer<typeof legalBlockSchema>;

// Operation Note fields schema
export const operationNoteFieldSchema = documentInformationFieldSchema.pick({
  dateOfEvent: true,
  specialty: true,
});

export type OperationNoteFields = z.infer<typeof operationNoteFieldSchema>;
export const operationNoteBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.OPERATION_NOTE),
  documentExtractedFields: z.lazy(() => operationNoteFieldSchema),
  initialReviewedFields: z.lazy(() => operationNoteFieldSchema).nullable(),
  reviewedFields: z.lazy(() => operationNoteFieldSchema).nullable(),
  metadata: operationNoteBlockMetadataSchema.nullable(),
});
export type OperationNoteBlock = z.infer<typeof operationNoteBlockSchema>;

// Procedure fields schema
export const procedureFieldSchema = documentInformationFieldSchema.pick({
  ahi: true,
  dateOfEvent: true,
  dateReceived: true,
  facilityType: true,
  isIcd: true,
  isSigned: true,
  procedure: true,
  procedureCardiacType: true,
  procedureDeviceType: true,
  recommendation: true,
  studyType: true,
  summaryStatements: true,
});

export type ProcedureFields = z.infer<typeof procedureFieldSchema>;
export const procedureBlockSchema = baseBlockSchema.extend({
  type: z.literal(BlockType.PROCEDURE),
  documentExtractedFields: z.lazy(() => procedureFieldSchema),
  initialReviewedFields: z.lazy(() => procedureFieldSchema).nullable(),
  reviewedFields: z.lazy(() => procedureFieldSchema).nullable(),
  metadata: procedureBlockMetadataSchema.nullable(),
});
export type ProcedureBlock = z.infer<typeof procedureBlockSchema>;

// Generic block type
export type Block =
  | PatientBlock
  | CoverageBlock
  | ProviderBlock
  | LocationBlock
  | ReferralInformationBlock
  | DocumentInformationBlock
  | PriorAuthorizationBlock
  | ProgressNoteBlock
  | ImagingResultsBlock
  | LabResultsBlock
  | AdminBlock
  | ContinuityOfCareBlock
  | MedicalHistoryBlock
  | MedicalRecordRequestBlock
  | NotificationBlock
  | PharmacyRelatedBlock
  | EmergencySummaryBlock
  | ReferralResponseBlock
  | CarePlanBlock
  | DmeBlock
  | ClearanceRequestBlock
  | ConsentBlock
  | FinancialBlock
  | IdCardBlock
  | InsuranceBlock
  | IntakeBlock
  | LegalBlock
  | OperationNoteBlock
  | ProcedureBlock;

// Typed paths
type PatientReviewedFields = z.infer<typeof patientBlockSchema>["reviewedFields"];
type CoverageReviewedFields = z.infer<typeof coverageBlockSchema>["reviewedFields"];
type ProviderReviewedFields = z.infer<typeof providerBlockSchema>["reviewedFields"];
type LocationReviewedFields = z.infer<typeof locationBlockSchema>["reviewedFields"];
type ReferralInformationReviewedFields = z.infer<typeof referralInformationBlockSchema>["reviewedFields"];
type DocumentInformationReviewedFields = z.infer<typeof documentInformationBlockSchema>["reviewedFields"];
type PriorAuthorizationReviewedFields = z.infer<typeof priorAuthorizationBlockSchema>["reviewedFields"];
type ProgressNoteReviewedFields = z.infer<typeof progressNoteBlockSchema>["reviewedFields"];
type ImagingResultsReviewedFields = z.infer<typeof imagingResultsBlockSchema>["reviewedFields"];
type LabResultsReviewedFields = z.infer<typeof labResultsBlockSchema>["reviewedFields"];
type AdminReviewedFields = z.infer<typeof adminBlockSchema>["reviewedFields"];
type ContinuityOfCareReviewedFields = z.infer<typeof continuityOfCareBlockSchema>["reviewedFields"];
type MedicalHistoryReviewedFields = z.infer<typeof medicalHistoryBlockSchema>["reviewedFields"];
type MedicalRecordRequestReviewedFields = z.infer<typeof medicalRecordRequestBlockSchema>["reviewedFields"];
type NotificationReviewedFields = z.infer<typeof notificationBlockSchema>["reviewedFields"];
type PharmacyRelatedReviewedFields = z.infer<typeof pharmacyRelatedBlockSchema>["reviewedFields"];
type EmergencySummaryReviewedFields = z.infer<typeof emergencySummaryBlockSchema>["reviewedFields"];
type ReferralResponseReviewedFields = z.infer<typeof referralResponseBlockSchema>["reviewedFields"];
type CarePlanReviewedFields = z.infer<typeof carePlanBlockSchema>["reviewedFields"];
type DmeReviewedFields = z.infer<typeof dmeBlockSchema>["reviewedFields"];
type ClearanceRequestReviewedFields = z.infer<typeof clearanceRequestBlockSchema>["reviewedFields"];
type ConsentReviewedFields = z.infer<typeof consentBlockSchema>["reviewedFields"];
type FinancialReviewedFields = z.infer<typeof financialBlockSchema>["reviewedFields"];
type IdCardReviewedFields = z.infer<typeof idCardBlockSchema>["reviewedFields"];
type InsuranceReviewedFields = z.infer<typeof insuranceBlockSchema>["reviewedFields"];
type IntakeReviewedFields = z.infer<typeof intakeBlockSchema>["reviewedFields"];
type LegalReviewedFields = z.infer<typeof legalBlockSchema>["reviewedFields"];
type OperationNoteReviewedFields = z.infer<typeof operationNoteBlockSchema>["reviewedFields"];
type ProcedureReviewedFields = z.infer<typeof procedureBlockSchema>["reviewedFields"];

// Address field keys for nested address paths
type AddressFieldKey = keyof z.infer<typeof addressSchema>;

export type PatientFieldKey = keyof NonNullable<PatientReviewedFields>;
export type CoverageFieldKey = keyof NonNullable<CoverageReviewedFields>;
export type ProviderFieldKey = keyof NonNullable<ProviderReviewedFields>;
export type LocationFieldKey = keyof NonNullable<LocationReviewedFields>;
export type ReferralInformationFieldKey = keyof NonNullable<ReferralInformationReviewedFields>;
export type DocumentInformationFieldKey = keyof NonNullable<DocumentInformationReviewedFields>;
export type PriorAuthorizationFieldKey = keyof NonNullable<PriorAuthorizationReviewedFields>;
export type ProgressNoteFieldKey = keyof NonNullable<ProgressNoteReviewedFields>;
export type ImagingResultsFieldKey = keyof NonNullable<ImagingResultsReviewedFields>;
export type LabResultsFieldKey = keyof NonNullable<LabResultsReviewedFields>;

// BlockKey-based field paths
export type PatientFieldPath = `${BlockKey.PATIENT}.${PatientFieldKey}`;

export type PrimaryCoverageFieldPath = `${BlockKey.PRIMARY_COVERAGE}.${CoverageFieldKey}`;
export type SecondaryCoverageFieldPath = `${BlockKey.SECONDARY_COVERAGE}.${CoverageFieldKey}`;

export type OrderingProviderFieldPath = `${BlockKey.ORDERING_PROVIDER}.${ProviderFieldKey}`;
export type PrimaryCareProviderFieldPath = `${BlockKey.PRIMARY_CARE_PROVIDER}.${ProviderFieldKey}`;
export type ReferringProviderFieldPath = `${BlockKey.REFERRING_PROVIDER}.${ProviderFieldKey}`;
export type ReceivingProviderFieldPath = `${BlockKey.RECEIVING_PROVIDER}.${ProviderFieldKey}`;
export type ProviderFieldPath = `${BlockKey.PROVIDER}.${ProviderFieldKey}`; // Generic provider block

export type ReferringLocationFieldPath =
  | `${BlockKey.REFERRING_LOCATION}.${LocationFieldKey}`
  | `${BlockKey.REFERRING_LOCATION}.address.${AddressFieldKey}`;
export type ReceivingLocationFieldPath =
  | `${BlockKey.RECEIVING_LOCATION}.${LocationFieldKey}`
  | `${BlockKey.RECEIVING_LOCATION}.address.${AddressFieldKey}`;

export type ReferralInformationFieldPath = `${BlockKey.REFERRAL_INFORMATION}.${ReferralInformationFieldKey}`;
export type DocumentInformationFieldPath = `${BlockKey.DOCUMENT_INFORMATION}.${DocumentInformationFieldKey}`;
export type PriorAuthorizationFieldPath = `${BlockKey.PRIOR_AUTHORIZATION}.${PriorAuthorizationFieldKey}`;
export type ProgressNoteFieldPath = `${BlockKey.PROGRESS_NOTE}.${ProgressNoteFieldKey}`;
export type ImagingResultsFieldPath = `${BlockKey.IMAGING_RESULTS}.${ImagingResultsFieldKey}`;
export type LabResultsFieldPath = `${BlockKey.LAB_RESULTS}.${LabResultsFieldKey}`;

export type ReferralFieldPath =
  | PatientFieldPath
  | PrimaryCoverageFieldPath
  | SecondaryCoverageFieldPath
  | PrimaryCareProviderFieldPath
  | ReferringProviderFieldPath
  | ReceivingProviderFieldPath
  | ReferringLocationFieldPath
  | ReceivingLocationFieldPath
  | ReferralInformationFieldPath;

// Map of BlockKey to the corresponding reviewedFields shape.
export type BlockReviewedFieldsMap = {
  [BlockKey.PATIENT]: NonNullable<PatientReviewedFields>;

  // Provider-like blocks
  [BlockKey.PRIMARY_CARE_PROVIDER]: NonNullable<ProviderReviewedFields>;
  [BlockKey.REFERRING_PROVIDER]: NonNullable<ProviderReviewedFields>;
  [BlockKey.RECEIVING_PROVIDER]: NonNullable<ProviderReviewedFields>;
  [BlockKey.ORDERING_PROVIDER]: NonNullable<ProviderReviewedFields>;
  [BlockKey.PROVIDER]: NonNullable<ProviderReviewedFields>;

  // Location-like blocks
  [BlockKey.REFERRING_LOCATION]: NonNullable<LocationReviewedFields>;
  [BlockKey.RECEIVING_LOCATION]: NonNullable<LocationReviewedFields>;

  // Coverage blocks
  [BlockKey.PRIMARY_COVERAGE]: NonNullable<CoverageReviewedFields>;
  [BlockKey.SECONDARY_COVERAGE]: NonNullable<CoverageReviewedFields>;

  // Classification-specific blocks
  [BlockKey.REFERRAL_INFORMATION]: NonNullable<ReferralInformationReviewedFields>;
  [BlockKey.DOCUMENT_INFORMATION]: NonNullable<DocumentInformationReviewedFields>;
  [BlockKey.PRIOR_AUTHORIZATION]: NonNullable<PriorAuthorizationReviewedFields>;
  [BlockKey.PROGRESS_NOTE]: NonNullable<ProgressNoteReviewedFields>;
  [BlockKey.IMAGING_RESULTS]: NonNullable<ImagingResultsReviewedFields>;
  [BlockKey.LAB_RESULTS]: NonNullable<LabResultsReviewedFields>;
  [BlockKey.ADMIN]: NonNullable<AdminReviewedFields>;
  [BlockKey.CONTINUITY_OF_CARE]: NonNullable<ContinuityOfCareReviewedFields>;
  [BlockKey.MEDICAL_HISTORY]: NonNullable<MedicalHistoryReviewedFields>;
  [BlockKey.MEDICAL_RECORD_REQUEST]: NonNullable<MedicalRecordRequestReviewedFields>;
  [BlockKey.NOTIFICATION]: NonNullable<NotificationReviewedFields>;
  [BlockKey.PHARMACY_RELATED]: NonNullable<PharmacyRelatedReviewedFields>;
  [BlockKey.EMERGENCY_SUMMARY]: NonNullable<EmergencySummaryReviewedFields>;
  [BlockKey.REFERRAL_RESPONSE]: NonNullable<ReferralResponseReviewedFields>;
  [BlockKey.CARE_PLAN]: NonNullable<CarePlanReviewedFields>;
  [BlockKey.DME]: NonNullable<DmeReviewedFields>;
  [BlockKey.CLEARANCE_REQUEST]: NonNullable<ClearanceRequestReviewedFields>;
  [BlockKey.CONSENT]: NonNullable<ConsentReviewedFields>;
  [BlockKey.FINANCIAL]: NonNullable<FinancialReviewedFields>;
  [BlockKey.ID_CARD]: NonNullable<IdCardReviewedFields>;
  [BlockKey.INSURANCE]: NonNullable<InsuranceReviewedFields>;
  [BlockKey.INTAKE]: NonNullable<IntakeReviewedFields>;
  [BlockKey.LEGAL]: NonNullable<LegalReviewedFields>;
  [BlockKey.OPERATION_NOTE]: NonNullable<OperationNoteReviewedFields>;
  [BlockKey.PROCEDURE]: NonNullable<ProcedureReviewedFields>;
};

export type BlockReviewedFields<TBlockKey extends BlockKey> = TBlockKey extends keyof BlockReviewedFieldsMap
  ? BlockReviewedFieldsMap[TBlockKey]
  : never;

// Map of BlockKey to the corresponding Block type
export type BlockMap = {
  [BlockKey.PATIENT]: PatientBlock;

  // Provider-like blocks
  [BlockKey.PRIMARY_CARE_PROVIDER]: ProviderBlock;
  [BlockKey.REFERRING_PROVIDER]: ProviderBlock;
  [BlockKey.RECEIVING_PROVIDER]: ProviderBlock;
  [BlockKey.ORDERING_PROVIDER]: ProviderBlock;
  [BlockKey.PROVIDER]: ProviderBlock;

  // Location blocks
  [BlockKey.REFERRING_LOCATION]: LocationBlock;
  [BlockKey.RECEIVING_LOCATION]: LocationBlock;

  // Coverage blocks
  [BlockKey.PRIMARY_COVERAGE]: CoverageBlock;
  [BlockKey.SECONDARY_COVERAGE]: CoverageBlock;

  // Classification-specific blocks
  [BlockKey.REFERRAL_INFORMATION]: ReferralInformationBlock;
  [BlockKey.DOCUMENT_INFORMATION]: DocumentInformationBlock;
  [BlockKey.PRIOR_AUTHORIZATION]: PriorAuthorizationBlock;
  [BlockKey.PROGRESS_NOTE]: ProgressNoteBlock;
  [BlockKey.IMAGING_RESULTS]: ImagingResultsBlock;
  [BlockKey.LAB_RESULTS]: LabResultsBlock;
  [BlockKey.ADMIN]: AdminBlock;
  [BlockKey.CONTINUITY_OF_CARE]: ContinuityOfCareBlock;
  [BlockKey.MEDICAL_HISTORY]: MedicalHistoryBlock;
  [BlockKey.MEDICAL_RECORD_REQUEST]: MedicalRecordRequestBlock;
  [BlockKey.NOTIFICATION]: NotificationBlock;
  [BlockKey.PHARMACY_RELATED]: PharmacyRelatedBlock;
  [BlockKey.EMERGENCY_SUMMARY]: EmergencySummaryBlock;
  [BlockKey.REFERRAL_RESPONSE]: ReferralResponseBlock;
  [BlockKey.CARE_PLAN]: CarePlanBlock;
  [BlockKey.DME]: DmeBlock;
  [BlockKey.CLEARANCE_REQUEST]: ClearanceRequestBlock;
  [BlockKey.CONSENT]: ConsentBlock;
  [BlockKey.FINANCIAL]: FinancialBlock;
  [BlockKey.ID_CARD]: IdCardBlock;
  [BlockKey.INSURANCE]: InsuranceBlock;
  [BlockKey.INTAKE]: IntakeBlock;
  [BlockKey.LEGAL]: LegalBlock;
  [BlockKey.OPERATION_NOTE]: OperationNoteBlock;
  [BlockKey.PROCEDURE]: ProcedureBlock;
};

export type BlockForKey<TBlockKey extends BlockKey> = TBlockKey extends keyof BlockMap ? BlockMap[TBlockKey] : never;
